/**
 * Small shared helpers for the notification-center extras (reminders + comment
 * threads). Kept local because the notifications module owns its own copies and we
 * can't import across into it.
 */

/**
 * SQLite writes `datetime('now')` as 'YYYY-MM-DD HH:MM:SS' in UTC with no zone
 * marker. Safari refuses to parse that, and everyone else reads it as local time
 * and lands hours off. Normalise before trusting `new Date()`.
 */
export function parseTs(value: string): Date {
  const iso = value.includes('T') ? value : value.replace(' ', 'T');
  const zoned = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(zoned ? iso : `${iso}Z`);
}

/** "just now" / "5m ago" / "3h ago" / a clock time for older than a day. */
export function relativeTime(at: Date): string {
  const seconds = Math.max(0, (Date.now() - at.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** A friendly absolute due time, e.g. "Jul 14, 3:30 PM". */
export function formatDue(at: Date): string {
  return at.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Value for an <input type="datetime-local"> defaulted a couple hours out. */
export function defaultDueLocal(hoursOut = 2): string {
  const d = new Date(Date.now() + hoursOut * 3600_000);
  d.setSeconds(0, 0);
  // Shift by the local offset so toISOString's slice reads as local wall-clock.
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
